import type { MouseEvent } from "react";
import type { ClientTabPath } from "@ferrivia/analytics";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { NavLink } from "react-router";
import { tabsFor } from "../../app/routes";
import { useTabNavigation } from "../../app/useTabNavigation";
import { ProfileMenu } from "../ProfileMenu";
import s from "./NavRail.module.css";

function itemClass({ isActive }: { isActive: boolean }) {
  return isActive ? `${s.item} ${s.itemActive}` : s.item;
}

/**
 * The persistent left sidebar that replaces the floating pill above 960px —
 * see `TabBar.tsx`, which this mirrors. Renders the same `TABS` list into a
 * different chrome rather than owning a second destination list, and goes
 * through the same `goToTab` history discipline so desktop navigation stays
 * inside `TabNavigation.tsx`'s `[/, <tab>]` stack shape.
 */
export function NavRail() {
  const { t } = useTranslation("client");
  const market = useMarket();
  const { goToTab } = useTabNavigation();

  const labels: Record<string, string> = {
    "nav.home": t("nav.home", "Etusivu"),
    "nav.infusion": t("nav.infusion", "Rautainfuusio"),
    "nav.support": t("nav.support", "Kartoitus"),
    "nav.results": t("nav.results", "Tulokset"),
    "nav.chat": t("nav.chat", "Chat"),
  };

  function handleClick(event: MouseEvent<HTMLAnchorElement>, path: ClientTabPath) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    goToTab(path, "nav_rail");
  }

  return (
    <nav className={s.root} data-testid="nav-rail">
      <div className={s.brandmark}>
        <span className={s.dot} aria-hidden="true">
          F
        </span>
        <span className={s.brandName}>Ferrivia</span>
      </div>

      {tabsFor(market).map(({ path, labelKey, icon: Icon }) => (
        <NavLink
          key={path}
          to={path}
          end={path === "/"}
          className={itemClass}
          onClick={(event) => handleClick(event, path)}
        >
          <span className={s.icon} aria-hidden="true">
            <Icon size={22} strokeWidth={1.75} />
          </span>
          <span className={s.label}>{labels[labelKey]}</span>
        </NavLink>
      ))}

      <div className={s.spacer} />

      <ProfileMenu className={s.profile} data-testid="nav-rail-profile-menu" />
    </nav>
  );
}
